// Occurrence counting for recurring scenario transactions. Mirrors the stepping
// used by the scenario engines (frontend `scenarioEngine.ts` and backend
// `ScenarioEngineService.GenerateOccurrences`) so previews and forecasts agree.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toMidnight(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function stepMonthsFor(frequency: string): number {
  if (frequency === "quarterly") return 3;
  if (frequency === "annually") return 12;
  return 1;
}

/**
 * Count the occurrences of a recurring transaction inside [start, end]
 * (inclusive). Daily = every day, weekly = every 7 days, monthly/quarterly/
 * annually = calendar stepping respecting `monthlyDateMode`.
 */
export function countOccurrences(
  start: Date,
  end: Date,
  frequency: string,
  monthlyDateMode: string = "same-date",
): number {
  const from = toMidnight(start);
  const to = toMidnight(end);
  if (to.getTime() < from.getTime()) return 0;

  if (frequency === "one-off") return 1;

  const days = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;
  if (frequency === "daily") return days;
  if (frequency === "weekly") return Math.ceil(days / 7);

  const step = stepMonthsFor(frequency);
  let count = 0;
  const cursor = new Date(from);
  const originalDay = cursor.getDate();

  while (cursor.getTime() <= to.getTime()) {
    count++;
    cursor.setMonth(cursor.getMonth() + step);
    if (monthlyDateMode === "last-day") {
      cursor.setDate(1);
      cursor.setMonth(cursor.getMonth() + 1);
      cursor.setDate(0);
    } else {
      const daysInMonth = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        0,
      ).getDate();
      cursor.setDate(Math.min(originalDay, daysInMonth));
    }
  }
  return count;
}
